import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { buildStrategySimulationInput, strategyPlanUsage } from '../domain/raidStrategyRecommendation.js';
import { findBossTetrisCombinations } from '../domain/raidTetrisEngine.js';
import { RaidTetrisMiniDeck } from './RaidTetrisMiniDeck.jsx';

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function itemLabel(item = {}) {
  return String(item.title || '').trim() || `모의전 ${numberValue(item.slotNo, 1)}`;
}

function bossStatusText(target) {
  if (!target) return '';
  if (target.targetReached) return '처치 완료';
  if (target.remaining > 0) return `부족 ${formatRaidHpEok(target.remaining)}`;
  return '계획 충족';
}

function SlotRow({ item }) {
  return (
    <li>
      <span><strong>{item.nickname}</strong><small>{itemLabel(item)}</small></span>
      <RaidTetrisMiniDeck deck={item.deck || []} compact />
      <b>{formatEok(item.damageEok ?? item.damage)}</b>
    </li>
  );
}

export function RaidStrategyRecommendDialog({
  open = false,
  mobile = false,
  bosses = [],
  phaseNo = 1,
  initialBossNo = 0,
  candidates = [],
  targetsByBoss = new Map(),
  planUsage = { memberUsage: new Map(), memberUsedCodes: new Map() },
  effectiveAttacks = [],
  activePlans = [],
  actualRecords = [],
  memberCount = 0,
  recommendMode = 'nearest',
  onRecommendModeChange = () => {},
  preferAvailability = false,
  onPreferAvailabilityChange = () => {},
  currentAvailabilityOnly = false,
  onCurrentAvailabilityOnlyChange = () => {},
  canManage = false,
  onApplyCombo = () => '',
  onApplyPlan = () => '',
  onClose = () => {},
}) {
  const [scope, setScope] = useState('single');
  const [bossNo, setBossNo] = useState(initialBossNo || bosses[0]?.bossNo || 1);
  const [allPhases, setAllPhases] = useState(false);
  const [planIndex, setPlanIndex] = useState(0);
  const [applyNotice, setApplyNotice] = useState('');
  const [calculation, setCalculation] = useState({ loading: false, result: null, error: '', durationMs: 0 });
  const workerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setScope('single');
    setPlanIndex(0);
    setApplyNotice('');
    if (initialBossNo) setBossNo(Number(initialBossNo));
  }, [initialBossNo, open]);

  useEffect(() => setApplyNotice(''), [bossNo, scope]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  const phases = allPhases ? [1, 2, 3] : [numberValue(phaseNo, 1)];
  const simulationInput = useMemo(() => (open && scope === 'all' ? buildStrategySimulationInput({
    bosses,
    plans: activePlans,
    actualRecords,
    candidates,
    phases,
    fitMode: recommendMode === 'nearest' ? 'nearest' : 'kill',
    memberCount,
  }) : null), [actualRecords, activePlans, allPhases, bosses, candidates, memberCount, open, phaseNo, recommendMode, scope]);

  // Content key, not object identity: the board rebuilds candidates and plans on every silent raid
  // refresh, and restarting a seconds-long run under the leader each time would be unusable.
  const inputSignature = useMemo(() => (simulationInput ? JSON.stringify({
    fitMode: simulationInput.fitMode,
    targets: simulationInput.targets.map((target) => `${target.id}:${target.remaining}`),
    candidates: simulationInput.candidates.map((candidate) => `${candidate.id}:${candidate.damage}`),
    usage: [...simulationInput.initialUsage.entries()].map(([key, count]) => `${key}:${count}`).sort(),
    usedCodes: [...simulationInput.initialDeckKeys.entries()].map(([key, codes]) => `${key}:${[...codes].sort().join('/')}`).sort(),
  }) : ''), [simulationInput]);

  useEffect(() => {
    if (!simulationInput) return undefined;
    if (!simulationInput.targets.length) {
      setCalculation({ loading: false, result: null, error: '', durationMs: 0 });
      return undefined;
    }
    const requestId = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const worker = new Worker(new URL('../workers/raidFullSimulatorWorker.js', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    let active = true;
    setCalculation((current) => ({ ...current, loading: true, error: '' }));

    worker.addEventListener('message', (event) => {
      if (!active || event.data?.requestId !== requestId) return;
      if (event.data?.error) {
        setCalculation({ loading: false, result: null, error: String(event.data.error), durationMs: 0 });
      } else {
        setCalculation({ loading: false, result: event.data?.result || null, error: '', durationMs: numberValue(event.data?.durationMs) });
        setPlanIndex(0);
      }
    });
    worker.addEventListener('error', () => {
      if (active) setCalculation({ loading: false, result: null, error: '추천 계산 작업자를 실행하지 못했습니다.', durationMs: 0 });
    });
    worker.postMessage({ requestId, input: simulationInput });

    return () => {
      active = false;
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };
  }, [inputSignature]);

  useEffect(() => () => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  const singleTarget = targetsByBoss.get(Number(bossNo)) || null;
  // A boss the plan already fills has nothing missing, so the old code had nothing to recommend and
  // showed a dead end. But a plan is a proposal — only a real kill closes a boss. So when the plan is
  // what covers it, aim at the damage the real raid still owes and put this boss's own planned decks
  // back in the pool, which is what makes other combinations possible at all.
  const replansPlan = Boolean(singleTarget)
    && !singleTarget.targetReached
    && singleTarget.remaining <= 0
    && singleTarget.remainingAfterActualEok > 0;
  const singleUsage = useMemo(() => (replansPlan
    ? strategyPlanUsage(effectiveAttacks, { bossNo: Number(bossNo), phaseNo: Number(phaseNo) })
    : planUsage), [bossNo, effectiveAttacks, phaseNo, planUsage, replansPlan]);

  const singleRecommendation = useMemo(() => {
    if (!open || scope !== 'single' || !singleTarget) return null;
    const targetHpEok = replansPlan ? singleTarget.remainingAfterActualEok : singleTarget.remaining;
    if (targetHpEok <= 0) return null;
    return findBossTetrisCombinations({
      candidates,
      bossNo: Number(bossNo),
      targetHpEok,
      mode: recommendMode,
      preferAvailability,
      memberUsage: singleUsage.memberUsage,
      memberUsedCodes: singleUsage.memberUsedCodes,
      topN: 3,
    });
  }, [bossNo, candidates, open, preferAvailability, recommendMode, replansPlan, scope, singleTarget, singleUsage]);

  const bossCandidateCount = useMemo(() => candidates.filter((candidate) => (
    Number(candidate?.bossNo) === Number(bossNo)
  )).length, [bossNo, candidates]);

  if (!open) return null;

  const simulationPlan = calculation.result?.plans?.[planIndex] || calculation.result?.best || null;
  const plannedAssignments = (simulationPlan?.assignments || []).filter((assignment) => assignment.items?.length);
  const addedHits = plannedAssignments.reduce((sum, assignment) => sum + assignment.items.length, 0);
  const addedMembers = new Set(plannedAssignments.flatMap((assignment) => assignment.items.map((item) => item.memberOpenid))).size;
  const clearedCount = (simulationPlan?.assignments || []).filter((assignment) => assignment.cleared && !assignment.alreadyCleared).length;
  const scopeTargets = simulationInput?.targets || [];
  const plannedTargetIds = new Set(plannedAssignments.map((assignment) => assignment.target.id));
  const untouchedTargets = simulationPlan ? scopeTargets.filter((target) => !plannedTargetIds.has(target.id)) : [];
  const singleResults = singleRecommendation?.results || [];
  const insufficient = singleRecommendation?.insufficient || null;

  const applyCombo = (items) => {
    // When the combo was built by rewriting this boss's plan, adding it on top would double the boss
    // up, so it replaces the planned attacks it was allowed to reuse.
    const error = onApplyCombo(Number(bossNo), items, { replace: replansPlan });
    setApplyNotice(error || (replansPlan
      ? `${items.length}타로 이 보스 계획을 다시 짰습니다. 저장해야 유니온에 공유됩니다.`
      : `${items.length}타를 계획표에 넣었습니다. 저장해야 유니온에 공유됩니다.`));
  };

  const dialog = (
    <div className={`raid-strategy-recommend-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="raid-strategy-recommend-dialog" role="dialog" aria-modal="true" aria-labelledby="raid-strategy-recommend-title">
        <header className="raid-strategy-recommend-head">
          <div>
            <span>TETRIS RECOMMEND</span>
            <strong id="raid-strategy-recommend-title">테트리스 추천 · {phaseNo}단</strong>
          </div>
          <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
        </header>

        <div className="raid-strategy-recommend-scope" role="tablist" aria-label="추천 범위">
          <button type="button" role="tab" aria-selected={scope === 'single'} className={scope === 'single' ? 'active' : ''} onClick={() => setScope('single')}><strong>단일 보스</strong><small>보스 하나의 부족분</small></button>
          <button type="button" role="tab" aria-selected={scope === 'all'} className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}><strong>전체 보스</strong><small>3타·니케를 공유해 한 번에</small></button>
        </div>

        <div className="raid-strategy-recommend-options">
          <label><span>추천 기준</span><select aria-label="추천 기준" value={recommendMode} onChange={(event) => onRecommendModeChange(event.target.value)}><option value="nearest">오차 최소</option><option value="kill">막타 최소</option></select></label>
          <div className="raid-strategy-recommend-availability">
            <span>후보 접속 상태</span>
            <div>
              {scope === 'single' ? <label><input type="checkbox" checked={preferAvailability} onChange={(event) => onPreferAvailabilityChange(event.target.checked)} />접속 가능 우선</label> : null}
              <label><input type="checkbox" checked={currentAvailabilityOnly} onChange={(event) => onCurrentAvailabilityOnlyChange(event.target.checked)} />지금 접속 가능한 멤버만</label>
            </div>
          </div>
        </div>

        <div className="raid-strategy-recommend-body">
          {applyNotice ? <p className="raid-strategy-recommend-apply-notice" role="status">{applyNotice}</p> : null}
          {scope === 'single' ? (
            <>
              <nav className="raid-strategy-recommend-bosses" aria-label="추천 보스 선택">
                {bosses.map((boss) => {
                  const target = targetsByBoss.get(Number(boss.bossNo));
                  const selected = Number(boss.bossNo) === Number(bossNo);
                  return (
                    <button type="button" key={boss.bossNo} className={`${selected ? 'active' : ''} ${target?.remaining > 0 ? 'lack' : 'ready'}`.trim()} aria-pressed={selected} onClick={() => setBossNo(Number(boss.bossNo))}>
                      <strong title={bossDisplayName(boss)}>{bossDisplayName(boss)}</strong>
                      <small>{bossStatusText(target)}</small>
                    </button>
                  );
                })}
              </nav>

              <section className="raid-strategy-recommend-summary">
                <article><span>목표 체력</span><strong>{formatRaidHpEok(singleTarget?.hp)}</strong></article>
                <article><span>실전</span><strong>{formatEok(singleTarget?.actualEok)}</strong></article>
                <article><span>기존 계획</span><strong>{formatEok(singleTarget?.plannedEok)}</strong></article>
                <article className={singleTarget?.remaining > 0 ? 'lack' : 'ready'}><span>남은 부족분</span><strong>{singleTarget?.remaining > 0 ? formatRaidHpEok(singleTarget.remaining) : '없음'}</strong></article>
              </section>

              {replansPlan ? (
                <p className="raid-strategy-recommend-replan">이 보스는 계획으로 체력을 채운 상태입니다. 실전으로 처치된 것은 아니므로, 기존 계획을 풀고 실전 기준 {formatRaidHpEok(singleTarget.remainingAfterActualEok)}을 채우는 다른 조합을 보여드립니다. 조합을 넣으면 이 보스의 기존 계획을 대신합니다.</p>
              ) : null}

              {!singleTarget || singleTarget.targetReached ? (
                <p className="raid-strategy-recommend-empty">이미 처치한 보스입니다. 추천할 슬롯이 없습니다.</p>
              ) : singleTarget.remaining <= 0 && !replansPlan ? (
                <p className="raid-strategy-recommend-empty">실전으로 목표 체력을 이미 채웠습니다.</p>
              ) : singleResults.length ? (
                <div className="raid-strategy-recommend-results">
                  {singleResults.map((result) => (
                    <article className="raid-strategy-recommend-combo" key={result.rank}>
                      <header>
                        <div><span>추천 {result.rank}</span><strong>{result.items.length}타 · 합계 {formatEok(result.totalEok)}</strong></div>
                        <small className={result.diffEok >= 0 ? 'over' : 'short'}>{result.diffEok >= 0 ? `초과 +${formatRaidHpEok(result.diffEok)}` : `부족 ${formatRaidHpEok(Math.abs(result.diffEok))}`}</small>
                      </header>
                      <ol>{result.items.map((item) => <SlotRow item={item} key={item.id} />)}</ol>
                      {canManage ? <button type="button" className="primary" onClick={() => applyCombo(result.items)}>이 조합 넣기</button> : null}
                    </article>
                  ))}
                </div>
              ) : insufficient ? (
                <div className="raid-strategy-recommend-results">
                  <article className="raid-strategy-recommend-combo short">
                    <header>
                      <div><span>목표 미달</span><strong>{insufficient.items.length}타 · 합계 {formatEok(insufficient.totalEok)}</strong></div>
                      <small className="short">부족 {formatRaidHpEok(insufficient.shortageEok)}</small>
                    </header>
                    <ol>{insufficient.items.map((item) => <SlotRow item={item} key={item.id} />)}</ol>
                    <p>남은 후보로는 이 보스를 처치할 수 없습니다. 그래도 넣으면 체력을 그만큼 깎습니다.</p>
                    {canManage ? <button type="button" onClick={() => applyCombo(insufficient.items)}>그래도 넣기</button> : null}
                  </article>
                </div>
              ) : (
                <p className="raid-strategy-recommend-empty">
                  {bossCandidateCount
                    ? '남은 모의전이 3타·니케 중복 제한에 걸려 조합을 만들 수 없습니다. 다른 단수 계획에서 슬롯을 비우거나 접속 조건을 바꿔보세요.'
                    : '이 보스에 쓸 수 있는 모의전 슬롯이 없습니다.'}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="raid-strategy-recommend-range" role="tablist" aria-label="전체 보스 계산 범위">
                <button type="button" role="tab" aria-selected={!allPhases} className={!allPhases ? 'active' : ''} onClick={() => setAllPhases(false)}>{phaseNo}단 5보스</button>
                <button type="button" role="tab" aria-selected={allPhases} className={allPhases ? 'active' : ''} onClick={() => setAllPhases(true)}>1~3단 전체</button>
              </div>

              {!scopeTargets.length ? (
                <p className="raid-strategy-recommend-empty">이 범위에서 더 채울 보스가 없습니다.</p>
              ) : calculation.error ? (
                <p className="raid-strategy-recommend-empty error">{calculation.error}</p>
              ) : !simulationPlan ? (
                <p className="raid-strategy-recommend-empty" role="status" aria-live="polite">{calculation.loading ? '남은 부족분을 한 번에 계산하는 중입니다…' : '계산 결과가 없습니다.'}</p>
              ) : (
                <>
                  <section className="raid-strategy-recommend-summary">
                    <article><span>대상 보스</span><strong>{scopeTargets.length}개</strong></article>
                    <article className={clearedCount >= scopeTargets.length ? 'ready' : 'lack'}><span>채워지는 보스</span><strong>{clearedCount}개</strong></article>
                    <article><span>추가 타수</span><strong>{addedHits}타</strong></article>
                    <article><span>참여 멤버</span><strong>{addedMembers}명</strong></article>
                  </section>

                  {calculation.result?.plans?.length > 1 ? (
                    <div className="raid-strategy-recommend-plans" role="tablist" aria-label="추천 계획 선택">
                      {calculation.result.plans.map((item, index) => (
                        <button type="button" role="tab" aria-selected={planIndex === index} className={planIndex === index ? 'active' : ''} key={item.signature || index} onClick={() => setPlanIndex(index)}>
                          계획 {index + 1}<small>{item.clearedCount}보스 · {item.usedHits}타</small>
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="raid-strategy-recommend-targets">
                    {plannedAssignments.map((assignment) => {
                      const target = assignment.target;
                      const boss = bosses.find((item) => Number(item.bossNo) === Number(target.bossNo)) || null;
                      return (
                        <article className={`raid-strategy-recommend-target ${assignment.cleared ? 'ready' : 'lack'}`} key={target.id}>
                          <header>
                            <div><span>{target.level}단</span><strong>{bossDisplayName(boss, target.bossNo)}</strong></div>
                            <small>부족 {formatRaidHpEok(target.remaining)} → {assignment.cleared ? (assignment.overkill > 0 ? `초과 +${formatRaidHpEok(assignment.overkill)}` : '딱 맞게 처치') : `부족 ${formatRaidHpEok(assignment.remaining)}`}</small>
                          </header>
                          <ol>{assignment.items.map((item) => <SlotRow item={item} key={item.id} />)}</ol>
                        </article>
                      );
                    })}
                  </div>

                  {untouchedTargets.length ? (
                    <p className="raid-strategy-recommend-note">남은 후보로 채우지 못한 보스 {untouchedTargets.length}개: {untouchedTargets.map((target) => `${target.level}단 ${target.bossName}`).join(', ')}</p>
                  ) : null}
                  <p className="raid-strategy-recommend-note">{calculation.loading ? '새로 계산하는 중입니다…' : `계산 ${(calculation.durationMs / 1000).toFixed(1)}초 · 단수 순서(1→3단)는 유지하고 같은 단수 안에서는 후보가 부족한 보스를 먼저 채웁니다.`}</p>
                </>
              )}
            </>
          )}
        </div>

        <footer className="raid-strategy-recommend-actions">
          <button type="button" onClick={onClose}>닫기</button>
          {scope === 'all' && canManage ? <button type="button" className="primary" disabled={!plannedAssignments.length || calculation.loading} onClick={() => onApplyPlan(plannedAssignments.map((assignment) => ({
            bossNo: Number(assignment.target.bossNo),
            phaseNo: Number(assignment.target.level),
            items: assignment.items,
          })))}>계획표에 적용 ({addedHits}타)</button> : null}
        </footer>
      </section>
    </div>
  );

  return createPortal(dialog, document.body);
}
